/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useRoleProfileForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing role profile form state and changes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { RoleProfileFormData } from '../types';

const initialFormData: RoleProfileFormData = {
  name: '',
  description: '',
  permissions: []
};

export function useRoleProfileForm(mode: 'create' | 'edit') {
  const [formData, setFormData] = useState<RoleProfileFormData>(initialFormData);
  const [originalData, setOriginalData] = useState<RoleProfileFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: keyof RoleProfileFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const resetForm = (data?: RoleProfileFormData, original?: RoleProfileFormData) => {
    if (mode === 'create') {
      setFormData(initialFormData);
      setOriginalData(initialFormData);
    } else if (data && original) {
      setFormData({ ...data });
      setOriginalData({ ...original });
    }
    setErrors({});
  };

  const hasChanges = useMemo(() => {
    if (mode === 'create') return true;

    // Compare basic fields
    if (formData.name !== originalData.name || formData.description !== originalData.description || formData.is_system !== originalData.is_system) {
      return true;
    }

    // Compare permissions (deep comparison)
    const currentPerms = JSON.stringify([...formData.permissions].sort((a, b) => a.moduleSlug.localeCompare(b.moduleSlug)));
    const originalPerms = JSON.stringify([...originalData.permissions].sort((a, b) => a.moduleSlug.localeCompare(b.moduleSlug)));

    return currentPerms !== originalPerms;
  }, [mode, formData, originalData]);

  return {
    formData,
    originalData,
    setFormData,
    setOriginalData,
    handleInputChange,
    resetForm,
    hasChanges,
    errors,
    setErrors,
  };
}

